import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import AuthContext from '../contexts/AuthContext';
import '../styles/QuestionnairePage.css';

const QuestionnairePage = () => {
  const [sections, setSections] = useState([]);
  const [responses, setResponses] = useState({});
  const [errors, setErrors] = useState({});
  const [submitError, setSubmitError] = useState(false);
  const navigate = useNavigate();
  const { user, token, logout } = useContext(AuthContext);

  useEffect(() => {
    if (!user || !token) {
      navigate('/');
      return;
    }
  
    const fetchSections = async () => {
      try {
        const response = await axios.get(`/api/questions`, {
          headers: { Authorization: `Bearer ${token}` },
          params: { surveyCategory: user.submission_count + 1 },
        });
        setSections(response.data.sections);
      } catch (error) {
        console.error('Error fetching questions:', error);
        logout();
      }
    };
  
    fetchSections();
  
    window.history.pushState(null, null, window.location.href);
    window.onpopstate = function () {
      window.history.pushState(null, null, window.location.href);
    };
  
    return () => {
      window.onpopstate = null;
    };
  }, [token, user, logout, navigate]);
  

  if (!user || user.submission_count === null || user.submission_count === undefined) {
    return null;
  }

  const handleChange = (questionId, value) => {
    setResponses({
      ...responses,
      [questionId]: value
    });

    if (value !== '') {
      setErrors({
        ...errors,
        [questionId]: false
      });
    }
  };

  const handleSubmitAndLogout = async () => {
    const newErrors = {};
  
    sections.forEach(section => {
      section.questions.forEach(question => {
        let isRequired = !question.is_optional;
  
        if (question.depends_on_question_id && question.depends_on_answer) {
          const dependentQuestionAnswer = responses[question.depends_on_question_id];
          if (dependentQuestionAnswer === question.depends_on_answer) {
            isRequired = true;
          } else {
            isRequired = false;
          }
        }
  
        if (isRequired && question.question_type !== 'multiple_choice' && !responses[question.question_id]) {
          newErrors[question.question_id] = true;
        }
      });
    });
  
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      setSubmitError(true);
      return;
    }
  
    try {
      await axios.post(`/api/submit-questionnaire`, {
        responses: Object.keys(responses).map(questionId => ({
          questionId: parseInt(questionId, 10),
          answer: responses[questionId]
        })),
        surveyCategory: user.submission_count + 1,
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
    
      navigate('/thankyou', { replace: true });
      setTimeout(logout, 5000);
    } catch (error) {
      console.error('Error submitting questionnaire:', error);
      alert('Failed to submit questionnaire');
    }    
  };    

  const renderQuestion = (question, sectionIndex, questionIndex) => {
    if (!question) return null;

    if (question.depends_on_question_id && question.depends_on_answer) {
      const dependentQuestionAnswer = responses[question.depends_on_question_id];
      if (dependentQuestionAnswer !== question.depends_on_answer) {
        return null;
      }
    }
  
    const questionNumber = `${sectionIndex + 1}.${questionIndex + 1}`;

    if (question.question_type === 'text') {
      const isLargeTextBox = [3, 17, 19, 26, 44, 57, 70, 83, 96].includes(question.question_id);
      return (
        <div className='text-question-input'>
          <p>{questionNumber}. {question.question_text}</p>
          {isLargeTextBox ? (
                  <textarea
                  value={responses[question.question_id] || ''}
                  onChange={(e) => handleChange(question.question_id, e.target.value)}
                  rows={5}
                  cols={50}
                  />
              ) : (
                  <input
                  type="text"
                  value={responses[question.question_id] || ''}
                  onChange={(e) => handleChange(question.question_id, e.target.value)}
                  />
          )}
          {errors[question.question_id] && <span style={{ color: 'red' }}>Diese Frage ist erforderlich.</span>}
        </div>
      );
    }
    
    if (question.question_type === 'number') {
      return (
        <div className="number-question-container">
          <p>{questionNumber}. {question.question_text}</p>
          <div className="number-question-input">
          <input
            type="number"
            min="0"
            max="10"
            value={responses[question.question_id] || ''}
            onChange={(e) => handleChange(question.question_id, e.target.value)}
            onInput={(e) => {
              const value = parseInt(e.target.value, 10);
              if (value < 0) {
                e.target.value = 0;
              } else if (value > 10) {
                e.target.value = 10;
              }
            }}
          />
            {question.image_url && (
              <img src={question.image_url} alt="Question related" className="number-question-image" />
            )}
          </div>
          {errors[question.question_id] && (
            <span style={{ color: 'red' }}>Diese Frage ist erforderlich und muss zwischen 1 und 10 liegen.</span>
          )}
        </div>
      );
    }    
    
    if (question.question_type === 'single_choice') {
      if (question.options && question.options.length > 0) {
        if (question.options[0].image_url) {
          return (
            <div className="single-choice-with-images">
              <p>{questionNumber}. {question.question_text}</p>
              <div className='single-choice-with-images-label'>
                <p className='left-label'>{question.left_label}</p>
                <div className="single-choice-item-container">
                  {question.options.map(option => (
                    <div key={option.question_option_id} className="single-choice-item">
                      <img src={option.image_url} alt={option.value} />
                      <label>
                        <input
                          type="radio"
                          value={option.value}
                          checked={responses[question.question_id] === option.value}
                          onChange={() => handleChange(question.question_id, option.value)}
                        />
                      </label>
                    </div>
                  ))}
                </div>
                <p className='right-label'>{question.right_label}</p>
              </div>
              {errors[question.question_id] && <span style={{ color: 'red' }}>Diese Frage ist erforderlich.</span>}
            </div>
          );
        } else {
          return (
            <div className="single-choice-text-only">
              <p>{questionNumber}. {question.question_text}</p>
              <div className='single-choice-text-only-label'>
                <p className='left-label'>{question.left_label}</p>
                <table>
                  <thead>
                    <tr>
                      {question.options.map(option => (
                        <th key={option.question_option_id}>{option.value}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      {question.options.map(option => (
                        <td key={option.question_option_id}>
                          <label>
                            <input
                              type="radio"
                              value={option.value}
                              checked={responses[question.question_id] === option.value}
                              onChange={() => handleChange(question.question_id, option.value)}
                            />
                          </label>
                        </td>
                      ))}
                    </tr>
                  </tbody>
                </table>
                <p className='right-label'>{question.right_label}</p>
              </div>
              {errors[question.question_id] && <span style={{ color: 'red' }}>Diese Frage ist erforderlich.</span>}
            </div>
          );
        }
      }
      return null;
    }

    if (question.question_type === 'multiple_choice') {
      const groupedOptions = question.options ? question.options.reduce((acc, option) => {
        acc[option.group_title] = acc[option.group_title] || [];
        acc[option.group_title].push(option);
        return acc;
      }, {}) : {};

      return (
        <div className="multiple-choice-container">
          <h4>{questionNumber}. {question.question_text}</h4>
          {Object.keys(groupedOptions).map(groupTitle => (
            <div className="group-container" key={groupTitle}>
              <h4>{groupTitle}</h4>
              {groupedOptions[groupTitle].map(option => (
                <label key={option.question_option_id}>
                  <input
                    type="checkbox"
                    value={option.value}
                    checked={(responses[question.question_id] || []).includes(option.value)}
                    onChange={(e) => {
                      const currentValues = responses[question.question_id] || [];
                      if (e.target.checked) {
                        handleChange(question.question_id, [...currentValues, option.value]);
                      } else {
                        handleChange(question.question_id, currentValues.filter(v => v !== option.value));
                      }
                    }}
                  />
                  {option.value}
                </label>
              ))}
            </div>
          ))}
        </div>
      );
    }

    if (question.question_type === 'dropdown') {
      return (
        <div className="dropdown-question">
          <p>{questionNumber}. {question.question_text}</p>
          <select
            value={responses[question.question_id] || ''}
            onChange={(e) => handleChange(question.question_id, e.target.value)}
          >
            <option value="" disabled>Select an option</option>
            {question.options.map(option => (
              <option key={option.question_option_id} value={option.value}>
                {option.value}
              </option>
            ))}
          </select>
          {errors[question.question_id] && <span style={{ color: 'red' }}>Diese Frage ist erforderlich.</span>}
        </div>
      );
    }

    if (question.question_type === 'long_single_choice') {
      return (
        <div className="long-single-choice-question">
          <p>{questionNumber}. {question.question_text}</p>
          {question.options.map(option => (
            <div key={option.question_option_id} className="long-single-choice-option">
              <label>
                <input
                  type="radio"
                  value={option.value}
                  checked={responses[question.question_id] === option.value}
                  onChange={() => handleChange(question.question_id, option.value)}
                />
                {option.value}
              </label>
            </div>
          ))}
          {errors[question.question_id] && <span style={{ color: 'red' }}>Diese Frage ist erforderlich.</span>}
        </div>
      );
    }
  
    return null;
  };

  return (
    <div className="questionnaire-container">
      {user.submission_count === 0 && user.session_count === 0 && (
        <>
          <h3>Sehr geehrte Patientin,</h3>
          <p>Die Identifikation von Stressfaktoren während der Strahlentherapie gestaltet 
            sich als herausfordernd und ist bisher nicht ausreichend erforscht. 
            Um Patientinnen während der Behandlung optimal zu unterstützen, streben wir an, 
            die Ursachen von Stress sowie die damit verbundenen Situationen genauer zu verstehen. 
            Im Rahmen dieser Studie möchten wir Sie daher bitten, mehrere Fragebögen auszufüllen. 
            Bitte lesen Sie die Fragen sorgfältig und beantworten Sie diese dann.</p>
        </>
      )}
      {user.submission_count === 1 && user.session_count === 0 && (
        <>
          <h3>Sehr geehrte Patientin,</h3>
          <p>Sie kommen heute zur Bestrahlungsplanung. Wir interessieren uns dafür, wie Sie den 
            Prozess erleben und wie Sie sich dabei fühlen. Deshalb möchten wir Ihnen gerne ein paar 
            Fragen stellen. Bitte lesen Sie die Fragen sorgfältig durch und beantworten Sie diese dann.</p>
        </>
      )}
      {sections.map((section, sectionIndex) => (
        <div key={section.section_id} className="section-container">
          <h3>{sectionIndex + 1}. {section.section_title}</h3>
          <p>{section.section_guide}</p>
          {section.questions.map((question, questionIndex) => (
            <div key={question.question_id}>
              {renderQuestion(question, sectionIndex, questionIndex)}
            </div>
          ))}
        </div>
      ))}
      {submitError && (
        <div className="submit-error-message">
          <span style={{ color: 'red' }}>Bitte beantworten Sie vor dem Absenden alle erforderlichen Fragen.</span>
        </div>
      )}
      <button onClick={handleSubmitAndLogout}>Absenden</button>
    </div>
  );
};

export default QuestionnairePage;